import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft, FaPlay, FaHistory, FaListUl, FaTrash } from 'react-icons/fa';
import Btn from '../../components/ui/Btn';
import StatusTag from '../../components/ui/StatusTag';
import ConfirmModal from '../../components/ui/ConfirmModal';
import ToolCanvas from './editor/ToolCanvas';
import { useAuth } from '../../context/AuthContext';
import Inspector from './editor/Inspector';
import TestPanel from './editor/TestPanel';
import { toolsService } from '../../services/toolsService';
import { agentsService, friendlyError } from '../../services/agentsService';
import { NODE_TYPES, newNode, quickCheck, validSlug, updateNode, removeNode, moveNode, insertNode } from '../../utils/toolDefinition';
import Modal from '../../components/ui/Modal';
import ConnectorModal from '../Integrations/ConnectorModal';
import NodeView from './editor/NodeView';
import RunsPanel, { runLabel } from './editor/RunsPanel';
import { InsertProvider, ScopeProvider } from './editor/InsertContext';
import { engineService } from '../../services/engineService';
import { toolStatus } from './ToolsPage';
import { toastSuccess, toastError } from '../../utils/toast';
import DeleteToolModal from './DeleteToolModal';

const AUTOSAVE_MS = 900;
const sampleKey = (id) => `tool-sample-${id}`;
const readSample = (id) => { try { return JSON.parse(localStorage.getItem(sampleKey(id)) || '{}'); } catch { return {}; } };

// Editor de una herramienta: lienzo de pasos a la izquierda y configuración del paso elegido a la derecha. El borrador se guarda solo;
// los agentes usan la versión publicada, así que editar no cambia nada hasta «Publicar».
export default function ToolEditorPage() {
  const { toolId } = useParams();
  return <ToolEditor toolId={Number(toolId)} />;
}

/**
 * El editor. Suelto es una página (/herramientas/:id). `embedded`: se usa DENTRO de otra pantalla (por ejemplo la de un agente) sin
 * cambiar de ruta; `onExit` lo cierra (ya guardó el borrador) y quien lo abrió se queda donde estaba.
 */
export function ToolEditor({ toolId, embedded = false, onExit, backLabel = 'Volver' }) {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const isEmployee = currentUser?.role === 'employee';
  const [ownerOk, setOwnerOk] = useState(null);   // empleado: ¿la herramienta es personal de un agente suyo? (null = aún no se sabe)
  const [deleting, setDeleting] = useState(false);
  const id = toolId;
  const exit = async () => { await flush(); onExit?.(); };
  const [tool, setTool] = useState(null);
  const [draft, setDraft] = useState(null);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [state, setState] = useState('loading');
  const [error, setError] = useState('');
  const [save, setSave] = useState('saved');           // saved | dirty | saving | error
  const [saveError, setSaveError] = useState('');
  const [selected, setSelected] = useState(null);      // paso abierto en la vista de paso (null = ninguno)
  const [connectors, setConnectors] = useState([]);
  const [agentName, setAgentName] = useState('');
  const [testDefaults, setTestDefaults] = useState({});
  const [sample, setSample] = useState(() => readSample(id));
  // Datos de una ejecución: { nodes: {idPaso: {input, output, ok, ms…}}, inputs, meta }. La última prueba se conserva al editar (sirve
  // para armar la respuesta con datos reales) y durante la sesión del navegador; una ejecución abierta desde «Ejecuciones» la sustituye
  // mientras se mira.
  const lastKey = `tool-last-test-${id}`;
  const [lastTest, setLastTestState] = useState(() => { try { return JSON.parse(sessionStorage.getItem(lastKey) || 'null'); } catch { return null; } });
  const setLastTest = (r) => { setLastTestState(r); try { sessionStorage.setItem(lastKey, JSON.stringify(r)); } catch { /* sin almacenamiento o muy grande */ } };
  const [viewing, setViewing] = useState(null);       // ejecución abierta desde la lista
  const [runsOpen, setRunsOpen] = useState(false);
  const run = viewing || lastTest;
  const results = run?.nodes || null;
  const toRun = (nodes, inputs, meta) => ({ nodes: Object.fromEntries((nodes || []).map((n) => [n.id, n])), inputs: inputs || {}, meta });
  // Datos para la vista previa de las plantillas y para «Probar este paso»: la salida de cada paso (de la ejecución o la fijada).
  const scope = useMemo(() => {
    const pins = draft?.pinned || {};
    if (!run && !Object.keys(pins).length) return null;
    const nodos = { ...pins };
    for (const [k, n] of Object.entries(run?.nodes || {})) if (n.output !== undefined) nodos[k] = n.output;
    return { entrada: run?.inputs || {}, nodos };
  }, [run, draft?.pinned]);
  const [testing, setTesting] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [usage, setUsage] = useState(null);           // agentes que la usan (se pide al abrir «Publicar»)
  const [history, setHistory] = useState(false);
  const [creating, setCreating] = useState(null);     // { kind, then } mientras se crea un conector desde un paso
  const [projects, setProjects] = useState([]);
  const timer = useRef(null);
  const pending = useRef(null);

  const load = useCallback(async () => {
    try {
      const [t, c] = await Promise.all([toolsService.getTool(id), toolsService.listConnectors()]);
      if (!t) { setState('missing'); return; }
      setTool(t); setDraft(t.draft || {}); setName(t.name); setSlug(t.slug); setConnectors(c); setState('ready');
      agentsService.listProjects().then(setProjects).catch(() => {});
      if (isEmployee) setOwnerOk(t.scope === 'personal' ? null : false);
      if (t.agent_id) agentsService.getAgent(t.agent_id).then((d) => { if (isEmployee) setOwnerOk(Boolean(d?.agent?.created_by && d.agent.created_by === currentUser?.id)); setAgentName(d ? `${d.agent.name} · ${d.project?.name || ''}` : ''); if (d) setTestDefaults({ agent_id: String(d.agent.id), project_id: d.agent.project_id != null ? String(d.agent.project_id) : '' }); }).catch(() => { if (isEmployee) setOwnerOk(false); });
    } catch (e) { setError(e.code === 'missing_38' ? e.message : friendlyError(e)); setState('error'); }
  }, [id, isEmployee, currentUser?.id]);
  useEffect(() => { load(); }, [load]);

  const flush = useCallback(async () => {
    clearTimeout(timer.current);
    const p = pending.current; if (!p) return;
    pending.current = null; setSave('saving'); setSaveError('');
    try { await toolsService.updateTool(id, p); setSave((s) => (s === 'saving' ? 'saved' : s)); }
    catch (e) { setSaveError(e.message); setSave('error'); }
  }, [id]);
  useEffect(() => () => { clearTimeout(timer.current); }, []);
  const queue = (patch) => {
    pending.current = { ...(pending.current || {}), ...patch };
    setSave('dirty'); clearTimeout(timer.current); timer.current = setTimeout(flush, AUTOSAVE_MS);
  };
  const changeDraft = (d) => { setDraft(d); queue({ draft: d }); };
  const changeNode = (nodeId, patch) => changeDraft({ ...draft, nodes: updateNode(draft.nodes || [], nodeId, patch) });
  const onSample = (slot, connId) => { const s = { ...sample, [slot]: connId }; setSample(s); try { localStorage.setItem(sampleKey(id), JSON.stringify(s)); } catch { /* sin almacenamiento */ } };

  // `addr`: la lista donde va el paso ([] la principal; [idSiNo, 'then'|'else'] un camino). La respuesta queda siempre al final de su lista.
  const insert = (addr, index, type) => {
    const n = newNode(type, draft.nodes || []);
    const slot = (draft.connectors || []).find((s) => s.kind === ({ supabase: 'supabase', http: 'http', sheets: 'gsheets' })[type])?.slot;
    if (slot) n.slot = slot;
    changeDraft({ ...draft, nodes: insertNode(draft.nodes || [], addr, index, n) });
    setSelected(n.id);
  };
  // Fijar (o quitar con `undefined`) la salida de un paso: se guarda en el borrador (draft.pinned), nunca se publica (script 41).
  const pin = (nodeId, value) => {
    const next = { ...(draft.pinned || {}) };
    if (value === undefined) delete next[nodeId]; else next[nodeId] = value;
    if (JSON.stringify(next).length > 60_000) { toastError('Esos datos son muy grandes para fijarlos (máximo ~60 KB): fija una salida más chica.'); return; }
    changeDraft({ ...draft, pinned: next });
  };
  // Corre solo un paso con la salida de los anteriores (de la prueba o fijada) y lo agrega a los datos que se ven.
  const testOne = async (nodeId) => {
    await flush();
    const agentSlots = (draft.connectors || []).filter((x) => x.mode === 'agent');
    const r = await engineService.testTool({ toolId: id, definition: draft, connectors: Object.fromEntries(agentSlots.map((x) => [x.slot, sample[x.slot]])),
      only: nodeId, entrada: scope?.entrada || {}, nodos: scope?.nodos || {}, inputs: scope?.entrada || {} });
    const base = run || { nodes: {}, inputs: scope?.entrada || {}, meta: { label: 'pruebas de pasos sueltos', status: 'ok' } };
    const node = (r.nodes || []).find((n) => n.id === nodeId);
    setViewing(null);
    setLastTest({ ...base, nodes: { ...base.nodes, ...(node ? { [nodeId]: node } : {}) }, meta: { ...base.meta, label: `${base.meta?.label || 'tu prueba'} + «${node?.name || nodeId}» de nuevo`, status: r.ok ? base.meta?.status || 'ok' : 'error' } });
    if (!r.ok) throw new Error(r.error || 'El paso falló.');
  };
  const move = (nodeId, dir) => changeDraft({ ...draft, nodes: moveNode(draft.nodes || [], nodeId, dir) });
  const remove = (nodeId) => { changeDraft({ ...draft, nodes: removeNode(draft.nodes || [], nodeId) }); setSelected(null); };

  const warnings = useMemo(() => (draft ? quickCheck(draft) : {}), [draft]);
  const published = tool?.versions?.find((v) => v.version === tool.published_version);
  const noPins = (d) => { if (!d) return d; const { pinned: _p, ...rest } = d; return rest; };
  const unpublished = draft && (!published || JSON.stringify(noPins(published.definition)) !== JSON.stringify(noPins(draft)));
  const warnCount = Object.values(warnings).reduce((a, w) => a + w.length, 0);

  const openPublish = async () => {
    setUsage(null); setPublishing(true);
    try { setUsage(await toolsService.toolUsage(id)); } catch { setUsage([]); }
  };
  const restore = async (v) => {
    await flush();
    changeDraft(JSON.parse(JSON.stringify(v.definition)));
    setSelected(null); setHistory(false);
    toastSuccess(`La versión ${v.version} está en el borrador. Revísala y publícala para usarla.`);
  };
  const publish = async () => {
    await flush();
    try { const r = await toolsService.publishTool(id); toastSuccess(`Versión ${r.version} publicada`); await load(); }
    catch (e) { throw new Error(friendlyError(e)); }
  };

  if (state === 'loading') return <div className="max-w-7xl mx-auto" aria-busy="true"><div className="h-8 w-64 skeleton" /><div className="mt-6 h-[480px] skeleton rounded-3xl opacity-50" /></div>;
  if (state === 'missing') return <div className="max-w-5xl mx-auto text-sm text-fg-dim"><p className="text-fg font-medium">Esa herramienta no existe.</p>{embedded ? <button type="button" onClick={onExit} className="mt-3 inline-block text-live hover:underline">{backLabel}</button> : <Link to="/herramientas" className="mt-3 inline-block text-live hover:underline">Volver a Herramientas</Link>}</div>;
  if (state === 'error') return <div role="alert" className="max-w-5xl mx-auto rounded-xl border border-signal/30 bg-signal-soft p-5 text-sm"><p className="text-fg font-medium">No se pudo cargar la herramienta.</p><p className="mt-1 text-fg-dim">{error}</p></div>;

  if (isEmployee && ownerOk !== true) {
    if (ownerOk === null) return <div className="max-w-5xl mx-auto" aria-busy="true"><div className="h-8 w-64 skeleton" /></div>;
    return <ReadOnlyTool tool={tool} embedded={embedded} onExit={onExit} />;
  }

  const slugError = !validSlug(slug) ? 'Minúsculas, números y _; empieza con letra.' : '';
  const saveLabel = { saved: 'Borrador guardado', dirty: 'Cambios sin guardar…', saving: 'Guardando…', error: 'No se guardó' }[save];

  return (
    <InsertProvider>
    <ScopeProvider scope={scope}>
    <div className="max-w-7xl mx-auto">
      <DeleteToolModal tool={deleting ? tool : null} onClose={() => setDeleting(false)} onDeleted={async (t) => { toastSuccess(`«${t.name}» eliminada`); if (embedded) onExit?.({ deleted: true }); else navigate('/herramientas'); }} />
      <header className="flex flex-col lg:flex-row lg:items-center gap-4 pb-5 border-b border-line">
        {embedded ? (
          <button type="button" onClick={exit} className="inline-flex items-center gap-2 h-9 px-3.5 shrink-0 rounded-full text-[13px] font-semibold text-fg-dim hover:text-fg hover:bg-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60"><FaArrowLeft aria-hidden className="h-3 w-3" />{backLabel}</button>
        ) : (
          <Link to="/herramientas" aria-label="Volver a Herramientas" className="hidden lg:grid place-items-center h-9 w-9 shrink-0 rounded-full text-fg-dim hover:text-fg hover:bg-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60"><FaArrowLeft aria-hidden className="h-3 w-3" /></Link>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <input value={name} onChange={(e) => { setName(e.target.value); queue({ name: e.target.value.trim() || tool.name }); }} aria-label="Nombre de la herramienta" maxLength={80}
              className="font-display text-[24px] font-bold text-fg bg-transparent rounded-lg px-1 -mx-1 min-w-0 max-w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60" style={{ width: `${Math.max(name.length, 8) + 1}ch` }} />
            {toolStatus(tool)}
            {tool.published_version && unpublished && <StatusTag tone="caution">Cambios sin publicar</StatusTag>}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px] text-fg-dim">
            <label className="inline-flex items-center gap-1.5">Nombre para el agente
              <input value={slug} onChange={(e) => { const v = e.target.value.toLowerCase(); setSlug(v); if (validSlug(v)) queue({ slug: v }); }} spellCheck={false} aria-invalid={Boolean(slugError)} aria-describedby={slugError ? 'slug-err' : undefined}
                className={`font-mono text-[12.5px] bg-raised rounded-md px-1.5 h-6 ring-1 ${slugError ? 'ring-signal' : 'ring-line'} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60`} style={{ width: `${Math.max(slug.length, 6) + 2}ch` }} />
            </label>
            {slugError && <span id="slug-err" className="text-signal">{slugError}</span>}
            <span>{tool.scope === 'global' ? 'Global' : `Personal · ${agentName || `agente ${tool.agent_id}`}`}</span>
            <span className="font-mono text-fg-faint">{tool.public_id}</span>
            <span role="status" aria-live="polite" className={save === 'error' ? 'text-signal' : save === 'saved' ? 'text-live' : 'text-fg-faint'}>{saveLabel}{saveError ? `: ${saveError}` : ''}</span>
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <Btn variant="ghost" icon={FaTrash} onClick={() => setDeleting(true)} title="Eliminar esta herramienta">Eliminar</Btn>
          <Btn variant="ghost" icon={FaHistory} onClick={() => setHistory(true)} disabled={!tool.versions?.length}>Versiones</Btn>
          <Btn variant="ghost" icon={FaListUl} onClick={() => setRunsOpen((o) => !o)} aria-expanded={runsOpen}>Ejecuciones</Btn>
          <Btn icon={FaPlay} onClick={async () => { await flush(); setTesting(true); }}>Probar</Btn>
          <Btn variant="primary" onClick={openPublish} disabled={!unpublished || warnCount > 0 || save === 'error'} title={warnCount ? 'Completa lo que marca el editor antes de publicar' : !unpublished ? 'No hay cambios por publicar' : undefined}>
            {tool.published_version ? 'Publicar cambios' : 'Publicar'}
          </Btn>
        </div>
      </header>

      {warnCount > 0 && (
        <p role="note" className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl bg-caution-soft px-4 py-2.5 text-[13px] text-fg">
          <span>Para publicar falta completar {warnCount === 1 ? '1 cosa' : `${warnCount} cosas`} (los números amarillos del lienzo). Mientras siga en borrador, ningún agente la puede usar.</span>
          <button type="button" className="font-semibold text-caution underline rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60"
            onClick={() => setSelected(Object.keys(warnings).find((k) => k !== '_tool') && !warnings._tool ? Object.keys(warnings).find((k) => k !== '_tool') : '__entrada')}>Ir a lo que falta</button>
        </p>
      )}
      {run?.meta && (
        <p role="status" className={`mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl px-4 py-2.5 text-[13px] text-fg ${viewing ? 'bg-live-soft' : 'bg-raised'}`}>
          <span className={`h-2 w-2 rounded-full ${run.meta.status === 'ok' ? 'bg-live' : 'bg-signal'}`} aria-hidden />
          <span>{viewing ? 'Viendo la ejecución de' : 'Datos de'} {run.meta.label}{run.meta.status === 'ok' ? '' : ' · falló'}. Abre un paso para ver su entrada y su salida.</span>
          {viewing && <button type="button" className="font-semibold underline rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60" onClick={() => setViewing(null)}>{lastTest ? 'Volver a mi última prueba' : 'Dejar de verla'}</button>}
        </p>
      )}
      <div className="mt-5 h-[60vh] lg:h-[calc(100vh-250px)] min-h-[480px]">
        <ToolCanvas draft={draft} selected={selected} onSelect={setSelected} onInsert={insert} results={results} warnings={warnings} pinned={draft.pinned} />
      </div>
      {selected && (
        <NodeView nodeId={selected} draft={draft} run={run} onClose={() => setSelected(null)} onNavigate={setSelected} onTest={async () => { await flush(); setTesting(true); }}
          onTestOne={testOne} onPin={pin}>
          <Inspector draft={draft} tool={tool} selected={selected} onChangeDraft={changeDraft} onChangeNode={changeNode} onMove={move} onRemove={remove}
            connectors={connectors} sample={sample} onSample={onSample} warnings={warnings} onNewConnector={(kind, then) => setCreating({ kind, then })} results={results} />
        </NodeView>
      )}
      {runsOpen && <RunsPanel toolId={id} openId={viewing?.meta?.runId} onClose={() => setRunsOpen(false)}
        onOpen={(r) => setViewing(toRun(r.nodes, r.inputs, { label: runLabel(r), status: r.status, runId: r.id }))} />}

      <ConnectorModal open={Boolean(creating)} lockKind={creating?.kind} projects={projects} onClose={() => setCreating(null)}
        defaultScope={tool.scope === 'global' ? 'global' : tool.project_id ?? 'global'}
        onSaved={async (msg, r) => {
          const then = creating?.then; setCreating(null); toastSuccess(msg);
          try { setConnectors(await toolsService.listConnectors()); } catch { /* se ve al recargar */ }
          then?.(r);
        }} />
      <TestPanel scope={tool.scope} defaults={testDefaults} open={testing} onClose={() => setTesting(false)} draft={draft} sample={sample} toolId={id} pinned={draft.pinned}
        onResult={(r) => { setViewing(null); setLastTest(toRun(r.nodes, r.inputs, { label: `tu prueba de las ${new Date().toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}`, status: r.ok ? 'ok' : 'error', runId: r.run_id })); }} />
      <ConfirmModal open={publishing} onClose={() => setPublishing(false)} title={`Publicar la versión ${(tool.versions?.[0]?.version || 0) + 1}`} confirmLabel="Publicar" onConfirm={publish}>
        {usage === null ? <p aria-busy="true">Revisando qué agentes la usan…</p>
          : !usage.length ? <p>Ningún agente la tiene asignada todavía: publicarla no cambia nada de lo que responden.</p>
          : (
            <>
              <p>{usage.some((u) => u.live) ? 'Estos agentes la usan hoy y cambian desde su siguiente mensaje:' : 'La tienen asignada en su borrador (la usarán cuando se publiquen):'}</p>
              <ul className="list-disc pl-5">
                {usage.map((u) => <li key={u.id}>{embedded ? <span className="text-fg">{u.name}</span> : <Link to={`/agentes/${u.id}/herramientas`} className="text-live hover:underline">{u.name}</Link>}{u.live ? '' : ' (solo en el borrador)'}</li>)}
              </ul>
            </>
          )}
        <p>La versión anterior queda guardada y se puede volver a ella desde «Versiones». Antes de publicar, pruébala con «Probar».</p>
      </ConfirmModal>
      <Modal open={history} onClose={() => setHistory(false)} title="Versiones publicadas" width="max-w-xl"
        description="Cada publicación queda fija. Copiar una al borrador no cambia lo que usan los agentes hasta que vuelvas a publicar."
        footer={<Btn variant="ghost" onClick={() => setHistory(false)}>Cerrar</Btn>}>
        <ol className="divide-y divide-line rounded-xl ring-1 ring-line">
          {(tool.versions || []).map((v) => (
            <li key={v.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="text-[14px] font-semibold text-fg">Versión {v.version}{v.version === tool.published_version && <span className="ml-2 text-[12px] font-normal text-live">en uso</span>}</p>
                <p className="text-[12.5px] text-fg-dim">{new Date(v.published_at).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' })} · {countSteps(v.definition)} pasos · {(v.definition?.inputs || []).length} datos de entrada</p>
                <p className="text-[12.5px] text-fg-faint truncate">{v.definition?.description}</p>
              </div>
              <Btn size="sm" onClick={() => restore(v)}>Copiar al borrador</Btn>
            </li>
          ))}
        </ol>
      </Modal>
    </div>
    </ScopeProvider>
    </InsertProvider>
  );
}

const countSteps = (def) => { const walk = (l = []) => l.reduce((n, x) => n + 1 + (x.type === 'if' ? walk(x.then) + walk(x.else) : 0), 0); return walk(def?.nodes); };



// Una herramienta que la persona puede ver pero no editar (global, o de un agente de otra persona): sus pasos en una lista.
function ReadOnlyTool({ tool, embedded, onExit }) {
  const steps = [];
  const walk = (list, depth) => (list || []).forEach((n) => { steps.push({ n, depth }); walk(n.then, depth + 1); walk(n.else, depth + 1); });
  walk(tool.draft?.nodes, 0);
  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center gap-3">
        {embedded
          ? <button type="button" onClick={() => onExit?.()} className="h-9 px-3.5 rounded-full text-[13px] font-semibold text-fg-dim hover:text-fg hover:bg-raised">Volver</button>
          : <Link to="/herramientas" className="h-9 px-3.5 inline-flex items-center rounded-full text-[13px] font-semibold text-fg-dim hover:text-fg hover:bg-raised">Volver a Herramientas</Link>}
      </div>
      <h1 className="mt-4 font-display text-[28px] font-bold text-fg break-words">{tool.name}</h1>
      <p className="mt-1 text-[13px] text-fg-dim"><code className="text-fg-faint">{tool.slug}</code> · {tool.scope === 'global' ? 'Global' : 'Personal'} · {toolStatus(tool)}</p>
      <p role="note" className="mt-4 rounded-2xl bg-raised/60 ring-1 ring-line p-4 text-[13.5px] text-fg-dim">
        {tool.scope === 'global' ? 'Es una herramienta de la biblioteca: la administra un administrador. Puedes conectarla a tus agentes.' : 'Es de un agente de otra persona: solo puedes verla.'}
      </p>
      {tool.draft?.description && <p className="mt-4 text-[14.5px] text-fg">{tool.draft.description}</p>}
      <h2 className="mt-6 text-[15px] font-semibold text-fg">Pasos</h2>
      <ol className="mt-2 space-y-1.5">
        {steps.map(({ n, depth }, i) => (
          <li key={n.id || i} style={{ marginLeft: depth * 20 }} className="rounded-xl bg-panel ring-1 ring-line px-3.5 py-2.5 text-[13.5px] text-fg">
            <span className="font-semibold">{n.name || NODE_TYPES[n.type]?.label || n.type}</span>
            <span className="ml-2 text-fg-faint">{NODE_TYPES[n.type]?.label || n.type}</span>
          </li>
        ))}
        {!steps.length && <li className="text-[13.5px] text-fg-dim">Sin pasos.</li>}
      </ol>
    </div>
  );
}
